// ============================================================
// LHS PRODUCTION CORE - FRONTEND UTILITIES
// Single action runner, dedupe, and state helpers
// ============================================================

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================
// SINGLE ACTION RUNNER
// ============================================================
// Use this everywhere.
// Do not manually update status in React.
// Do not guess next action in React.
// Do not keep stale job objects.

export async function runJobAction(url, payload = null, options = {}) {
  const fetchOptions = {
    method: options.method || 'POST',
    headers: { 'Content-Type': 'application/json' },
  };

  if (payload) {
    fetchOptions.body = JSON.stringify(payload);
  }

  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
  const res = await fetch(fullUrl, fetchOptions);

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Action failed' }));
    throw new Error(err.detail || err.message || 'Action failed');
  }

  return res.json();
}

// ============================================================
// DEDUPE JOBS BEFORE RENDER
// ============================================================

export function dedupeJobs(jobs) {
  if (!Array.isArray(jobs)) return [];
  const map = new Map();
  jobs.forEach(job => {
    if (job && job.id) {
      map.set(job.id, job);
    }
  });
  return Array.from(map.values());
}

// ============================================================
// NEXT ACTION HELPERS
// ============================================================

export function getNextActionLabel(job) {
  return job?.next_action?.label || 'Review Job';
}

export function getNextActionType(job) {
  return job?.next_action?.action || 'review';
}

export function isTerminalState(job) {
  const status = job?.status?.toLowerCase();
  return status === 'closed' || status === 'cancelled';
}

export function canCancel(job) {
  return job && !isTerminalState(job);
}

// ============================================================
// STATUS COLORS
// ============================================================

export const STATUS_COLORS = {
  new: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  awaiting_quote: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  quote_submitted: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  approved: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
  in_progress: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  completed: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  invoiced: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
};

export function getStatusColor(status) {
  const key = status?.toLowerCase()?.replace(/\s+/g, '_');
  return STATUS_COLORS[key] || STATUS_COLORS.new;
}

// ============================================================
// PRIORITY COLORS
// ============================================================

export const PRIORITY_COLORS = {
  urgent: { bg: 'bg-red-500', text: 'text-white' },
  high: { bg: 'bg-orange-500', text: 'text-white' },
  medium: { bg: 'bg-yellow-500', text: 'text-black' },
  low: { bg: 'bg-green-500', text: 'text-white' },
};

export function getPriorityColor(priority) {
  const key = priority?.toLowerCase();
  return PRIORITY_COLORS[key] || PRIORITY_COLORS.medium;
}

// ============================================================
// SLA HELPERS
// ============================================================

export function getSLAColor(sla) {
  if (!sla?.at_risk) return "green";
  if (sla.level === "critical") return "red";
  if (sla.level === "warning") return "orange";
  return "gray";
}

export function getSLABadgeClass(sla) {
  const color = getSLAColor(sla);
  const classes = {
    green: "bg-green-100 text-green-800 border-green-300",
    orange: "bg-orange-100 text-orange-800 border-orange-300",
    red: "bg-red-100 text-red-800 border-red-300 animate-pulse",
    gray: "bg-gray-100 text-gray-800 border-gray-300",
  };
  return classes[color] || classes.gray;
}

export function formatSLAStatus(sla) {
  if (!sla) return "Unknown";
  if (!sla.at_risk) {
    return sla.hours_remaining 
      ? `${Math.round(sla.hours_remaining)}h remaining` 
      : "On track";
  }
  return `${Math.round(sla.hours_overdue)}h overdue`;
}

// ============================================================
// API ENDPOINTS - LHS CORE
// ============================================================

export const LHS_API = {
  // Jobs
  listJobs: (view = 'active', limit = 50, skip = 0) => 
    `${API_BASE}/api/lhs-core/jobs?view=${view}&limit=${limit}&skip=${skip}`,
  listJobsByStatus: (status, limit = 50, skip = 0) => 
    `${API_BASE}/api/lhs-core/jobs?status=${status}&limit=${limit}&skip=${skip}`,
  getJob: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}`,
  createJob: () => 
    `${API_BASE}/api/lhs-core/jobs`,
  
  // Actions
  assignContractors: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/assign-contractors`,
  submitQuote: (jobId, assignmentId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/assignments/${assignmentId}/quote`,
  approveQuote: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/approve-quote`,
  startJob: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/start`,
  completeJob: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/complete`,
  submitInvoice: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/invoice`,
  closeJob: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/close`,
  cancelJob: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/cancel`,
  
  // Evidence
  uploadPhotos: (jobId, assignmentId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/assignments/${assignmentId}/photos`,
  uploadJobSheet: (jobId, assignmentId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/assignments/${assignmentId}/job-sheet`,
  
  // Notes & Actions
  addAssignmentNote: (jobId, assignmentId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/assignments/${assignmentId}/notes`,
  addAssignmentAction: (jobId, assignmentId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/assignments/${assignmentId}/actions`,
  addInternalNote: (jobId) => 
    `${API_BASE}/api/lhs-core/jobs/${jobId}/internal-notes`,
  
  // Dashboard
  metrics: () => 
    `${API_BASE}/api/lhs-core/dashboard/metrics`,
  purchaseOrders: () => 
    `${API_BASE}/api/lhs-core/purchase-orders`,
  
  // Admin
  setupDemo: () => 
    `${API_BASE}/api/lhs-core/admin/setup-demo`,
  softReset: () => 
    `${API_BASE}/api/lhs-core/admin/soft-reset`,
  
  // SLA
  jobsWithSLA: (view = 'active', limit = 50) => 
    `${API_BASE}/api/lhs-core/jobs/with-sla?view=${view}&limit=${limit}`,
  jobsAtRisk: (level = null, limit = 50) => 
    `${API_BASE}/api/lhs-core/jobs/at-risk?limit=${limit}${level ? `&level=${level}` : ''}`,
  slaSummary: () => 
    `${API_BASE}/api/lhs-core/sla/summary`,
  
  // Client Dashboard
  clientOverview: (clientId = null) => 
    `${API_BASE}/api/lhs-core/client/overview${clientId ? `?client_id=${clientId}` : ''}`,
  clientJobs: (clientId = null, limit = 50) => 
    `${API_BASE}/api/lhs-core/client/jobs?limit=${limit}${clientId ? `&client_id=${clientId}` : ''}`,
  clientFinancials: (clientId = null, days = 30) => 
    `${API_BASE}/api/lhs-core/client/financials?days=${days}${clientId ? `&client_id=${clientId}` : ''}`,
};

export default {
  runJobAction,
  dedupeJobs,
  getNextActionLabel,
  getNextActionType,
  isTerminalState,
  canCancel,
  getStatusColor,
  getPriorityColor,
  LHS_API,
};
